import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { BankAccount } from '@/types/bankAccount';

import {
    getBankAccounts,
    postCreatePlaidBankAccount,
    postDeleteBankAccount,
    postFetchPlaidLinkToken,
} from './bankAccount.api';
import { shouldFetchBankAccounts } from './bankAccount.selectors';

export const fetchBankAccounts = createAsyncThunk<BankAccount[], void>(
    'la/ui/bankAccount/fetchBankAccounts',
    async (_, { getState }) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await getBankAccounts({
            authToken,
            bidderId,
            deployment,
        });
        return payload;
    }
);

export const fetchBankAccountsIfNeeded = createAsyncThunk<void, void>(
    'la/ui/bankAccount/fetchBankAccountsIfNeeded',
    async (_, { dispatch, getState }) => {
        const state = getState();
        if (shouldFetchBankAccounts(state)) {
            await dispatch(fetchBankAccounts());
        }
    }
);

type RemoveBankAccountParams = {
    bankAccountId: number;
    plaidAccountId?: string;
};

export const removeBankAccount = createAsyncThunk<void, RemoveBankAccountParams>(
    'la/ui/bankAccount/removeBankAccount',
    async ({ bankAccountId, plaidAccountId }, { dispatch, getState }) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        await postDeleteBankAccount({
            authToken,
            bankAccountId,
            bidderId,
            deployment,
            plaidAccountId,
        });

        await dispatch(fetchBankAccounts());
    }
);

type FetchBidderPlaidLinkTokenParams = {
    accessToken?: string;
    bidderId: number;
};

export const fetchBidderPlaidLinkToken = createAsyncThunk<string, FetchBidderPlaidLinkTokenParams>(
    'la/ui/bankAccount/fetchBidderPlaidLinkToken',
    async ({ accessToken = '', bidderId }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await postFetchPlaidLinkToken({
            accessToken,
            authToken,
            bidderId,
            deployment,
        });
        return payload;
    }
);

type AddPlaidBankAccountParams = {
    accessToken?: string;
    accountId: string;
    bidderId: number;
    firstName: string;
    lastName: string;
    token: string;
};

export const addPlaidBankAccount = createAsyncThunk<BankAccount, AddPlaidBankAccountParams>(
    'la/ui/bankAccount/addPlaidBankAccount',
    async ({ accessToken, accountId, bidderId, firstName, lastName, token }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await postCreatePlaidBankAccount({
            accessToken,
            accountId,
            authToken,
            bidderId,
            deployment,
            firstName,
            lastName,
            token,
        });

        return payload;
    }
);
